/**
 * Distance to leader along the course over race time (docs/backend/race-stats.md): leader on the
 * 0 line at the top, metres behind reading downward, one team-coloured line per boat, the leg
 * leader's mark roundings as vertical rules, and T / G / R / B letters on each boat's line.
 * Shares the hover cursor (grid index) with RaceCourseView.
 * A click calls `onSeek` with the grid index (RaceSummary opens the MAP at that moment).
 * With `leg` set: only that leg, from the leader's previous rounding (the start, on leg 1) to the last
 * boat's rounding at its end, each line re-based to the boat's distance behind at the leg's start, so
 * it reads metres lost (+, down) or gained (−, up) on the leg -- the leg gap breakdown's numbers.
 */
import { createEffect, createMemo, For, onCleanup, Show } from "solid-js";
import * as d3 from "d3";
import { chartInk } from "../../../utils/chartInk";
import { isDark } from "../../../store/themeStore";
import { sourcesStore } from "../../../store/sourcesStore";
import { formatRaceClock, legEndLabel, raceSecondsAt, type RaceTimelineRace } from "./raceTimeline";

const WIDTH = 860;
const HEIGHT = 560;
const M = { top: 34, right: 74, bottom: 36, left: 56 };

interface Props {
  race: RaceTimelineRace;
  sources: string[];
  cursor: number | null;
  onCursor: (i: number | null) => void;
  onSeek?: (i: number) => void;
  /** One leg (1-based) instead of the whole race. */
  leg?: number | null;
  /** What the y axis plots; the page owns the toggle (one control row above the map and this chart). */
}


export default function DtlChart(props: Props) {
  let svgRef: SVGSVGElement | undefined;
  /** Grid-index window of the selected leg: [leader's previous rounding (gun on leg 1), last boat off the leg]. */
  const legWindow = createMemo<[number, number] | null>(() => {
    const leg = props.leg;
    const race = props.race;
    if (leg == null) {return null;}
    const dt = race.dt || 1;
    const prev = race.legs.find((l) => l.leg === leg - 1)?.leader_end_offset_s;
    const lo = leg === 1 || prev == null ? Math.round((race.gun_offset_s ?? 0) / dt) : Math.round(prev / dt);
    const end = race.legs.find((l) => l.leg === leg)?.leader_end_offset_s;
    let hi = end != null ? Math.round(end / dt) : lo;
    for (const s of props.sources) {
      const legs = race.boats[s]?.leg ?? [];
      for (let i = legs.length - 1; i > hi; i--) {if (legs[i] === leg) {hi = i; break;}}
    }
    return hi > lo ? [lo, Math.min(hi, race.n - 1)] : null;
  });

  /** DTL per grid index; on a leg, minus the boat's DTL at the leg's start (leg 1: from 0) and null outside the leg. */
  const series = (s: string): (number | null)[] => {
    const dtl = props.race.boats[s].dtl_m;
    const w = legWindow();
    if (!w) {return dtl;}
    const base = props.leg === 1 ? 0 : dtl[w[0]];
    if (base == null) {return dtl.map(() => null);}
    return dtl.map((v, i) => (v == null || i < w[0] || i > w[1] ? null : v - base));
  };

  const geom = createMemo(() => {
    const race = props.race;
    const boats = props.sources.filter((s) => race.boats[s]);
    let maxAbs = 10;
    let minV = 0;
    for (const s of boats) {
      for (const v of series(s)) {
        if (v == null) {continue;}
        if (v > maxAbs) {maxAbs = v;}
        if (v < minV) {minV = v;}
      }
    }
    const w = legWindow();
    const t0 = raceSecondsAt(race, w ? w[0] : 0);
    const t1 = raceSecondsAt(race, w ? w[1] : race.n - 1);
    const x = d3.scaleLinear().domain([w ? t0 : Math.max(t0, -30), t1]).range([M.left, WIDTH - M.right]);
    // Leader on 0 at the top, metres behind reading down (on a leg, metres gained read up from 0).
    const y = d3.scaleLinear().domain([minV * 1.05, maxAbs * 1.05]).nice().range([M.top, HEIGHT - M.bottom]);
    return { boats, x, y };
  });

  const draw = () => {
    if (!svgRef) {return;}
    const ink = chartInk(isDark());
    const race = props.race;
    const { boats, x, y } = geom();
    const svg = d3.select(svgRef);
    svg.selectAll("*").remove();
    svg.attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);

    // Leg bands (alternate legs shaded) and the leg leader's roundings.
    const lastLeg = race.legs.length ? race.legs[race.legs.length - 1].leg : 0;
    let prev = Math.max(x.domain()[0], 0);
    const [d0, d1] = x.domain();
    race.legs.forEach((l, idx) => {
      if (l.leader_end_offset_s == null) {return;}
      const t = l.leader_end_offset_s - (race.gun_offset_s ?? 0);
      // A single leg: only the roundings inside its window, no alternate-leg shading.
      if (props.leg != null) {
        if (t < d0 || t > d1) {return;}
        svg.append("line").attr("x1", x(t)).attr("x2", x(t)).attr("y1", M.top).attr("y2", HEIGHT - M.bottom)
          .attr("stroke", ink.grid).attr("stroke-dasharray", "3 3");
        svg.append("text").attr("x", x(t)).attr("y", M.top - 8).attr("text-anchor", "middle")
          .attr("font-size", 10).attr("fill", ink.label).text(legEndLabel(l.leg, lastLeg, race.course));
        return;
      }
      if (idx % 2 === 1) {
        svg.append("rect").attr("x", x(prev)).attr("y", M.top).attr("width", Math.max(0, x(t) - x(prev)))
          .attr("height", HEIGHT - M.top - M.bottom).attr("fill", ink.grid).attr("opacity", 0.25);
      }
      svg.append("line").attr("x1", x(t)).attr("x2", x(t)).attr("y1", M.top).attr("y2", HEIGHT - M.bottom)
        .attr("stroke", ink.grid).attr("stroke-dasharray", "3 3");
      svg.append("text").attr("x", x(t)).attr("y", M.top - 8).attr("text-anchor", "middle")
        .attr("font-size", 10).attr("fill", ink.label).text(legEndLabel(l.leg, lastLeg, race.course));
      prev = t;
    });

    // Axes: recessive grid, y labels in metres behind.
    const yAxis = d3.axisLeft(y).ticks(6).tickSize(-(WIDTH - M.left - M.right)).tickFormat((v) => `${v}`);
    const gy = svg.append("g").attr("transform", `translate(${M.left},0)`).call(yAxis);
    gy.selectAll("line").attr("stroke", ink.grid);
    gy.selectAll("text").attr("fill", ink.axis).attr("font-size", 10);
    gy.select(".domain").remove();
    svg.append("text").attr("x", 12).attr("y", (M.top + HEIGHT - M.bottom) / 2).attr("font-size", 10)
      .attr("fill", ink.axis).attr("transform", `rotate(-90, 12, ${(M.top + HEIGHT - M.bottom) / 2})`)
      .attr("text-anchor", "middle").text(props.leg != null ? `METRES LOST ON LEG ${props.leg}` : "METRES BEHIND LEADER");
    const xAxis = d3.axisBottom(x).ticks(8).tickFormat((v) => formatRaceClock(Number(v)));
    const gx = svg.append("g").attr("transform", `translate(0,${HEIGHT - M.bottom})`).call(xAxis);
    gx.selectAll("text").attr("fill", ink.axis).attr("font-size", 10);
    gx.selectAll("line, .domain").attr("stroke", ink.frame);

    // Lines + maneuver letters + direct label at the line end.
    for (const s of boats) {
      const b = race.boats[s];
      const team = sourcesStore.getSourceColor(s) || ink.axis;
      const vals = series(s);
      const pts = vals.map((v, i) => ({ t: raceSecondsAt(race, i), v }));
      const visible = pts.filter((p) => p.t >= x.domain()[0] && p.t <= x.domain()[1]);
      svg.append("path")
        .attr("d", d3.line<{ t: number; v: number | null }>().defined((p) => p.v != null)
          .x((p) => x(p.t)).y((p) => y(p.v as number))(visible))
        .attr("fill", "none").attr("stroke", team).attr("stroke-width", 2).attr("stroke-linejoin", "round");
      const hits = b.mnvr.map((m, i) => (m && vals[i] != null ? { m, t: pts[i].t, v: vals[i] as number } : null))
        .filter((d): d is { m: string; t: number; v: number } => d != null && d.t >= x.domain()[0] && d.t <= x.domain()[1]);
      const g = svg.append("g");
      g.selectAll("circle").data(hits).join("circle").attr("cx", (d) => x(d.t)).attr("cy", (d) => y(d.v))
        .attr("r", 6).attr("fill", isDark() ? "#111" : "#fff").attr("stroke", team).attr("stroke-width", 1.5);
      g.selectAll("text").data(hits).join("text").attr("x", (d) => x(d.t)).attr("y", (d) => y(d.v) + 3)
        .attr("text-anchor", "middle").attr("font-size", 8).attr("font-weight", 700).attr("fill", ink.label)
        .text((d) => d.m);
      const last = [...visible].reverse().find((p) => p.v != null);
      if (last && boats.length <= 6) {
        svg.append("text").attr("x", x(last.t) + 6).attr("y", y(last.v as number) + 4)
          .attr("font-size", 11).attr("fill", ink.label).text(s);
      }
    }

    svg.append("g").attr("class", "dtl-cursor");
    svg.append("rect").attr("x", M.left).attr("y", M.top)
      .attr("width", WIDTH - M.left - M.right).attr("height", HEIGHT - M.top - M.bottom)
      .attr("fill", "transparent")
      .on("mousemove", (ev: MouseEvent) => {
        const [mx] = d3.pointer(ev);
        const sec = x.invert(mx);
        const i = Math.round((sec + (race.gun_offset_s ?? 0)) / (race.dt || 1));
        props.onCursor(i >= 0 && i < race.n ? i : null);
      })
      .on("mouseleave", () => props.onCursor(null))
      .on("click", (ev: MouseEvent) => {
        const [mx] = d3.pointer(ev);
        const i = Math.round((x.invert(mx) + (race.gun_offset_s ?? 0)) / (race.dt || 1));
        if (i >= 0 && i < race.n) {props.onSeek?.(i);}
      })
      .style("cursor", props.onSeek ? "pointer" : "default");
    drawCursor();
  };

  const drawCursor = () => {
    if (!svgRef) {return;}
    const layer = d3.select(svgRef).select<SVGGElement>("g.dtl-cursor");
    layer.selectAll("*").remove();
    const i = props.cursor;
    if (i == null) {return;}
    const ink = chartInk(isDark());
    const { boats, x, y } = geom();
    const t = raceSecondsAt(props.race, i);
    if (t < x.domain()[0] || t > x.domain()[1]) {return;}
    layer.append("line").attr("x1", x(t)).attr("x2", x(t)).attr("y1", M.top).attr("y2", HEIGHT - M.bottom)
      .attr("stroke", ink.crosshair).attr("stroke-width", 1);
    const rows = boats
      .map((s) => ({ s, v: series(s)[i] }))
      .filter((r): r is { s: string; v: number } => r.v != null)
      .sort((a, b) => a.v - b.v);
    for (const r of rows) {
      layer.append("circle").attr("cx", x(t)).attr("cy", y(r.v)).attr("r", 5)
        .attr("fill", sourcesStore.getSourceColor(r.s) || ink.axis).attr("stroke", ink.pointRing).attr("stroke-width", 2);
    }
    // Tooltip: time + each boat's metres behind, flipped left near the right edge.
    const unitText = (v: number) =>
      props.leg != null ? `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))} m` : `${Math.round(v)} m`;
    const lines = [formatRaceClock(t), ...rows.map((r) => `${r.s}  ${unitText(r.v)}`)];
    const right = x(t) > WIDTH - 220;
    const tx = right ? x(t) - 10 : x(t) + 10;
    const box = layer.append("g").attr("transform", `translate(${tx},${M.top + 10})`);
    const bg = box.append("rect").attr("rx", 4).attr("fill", isDark() ? "rgba(17,17,17,0.92)" : "rgba(255,255,255,0.95)")
      .attr("stroke", ink.grid);
    lines.forEach((l, k) => {
      box.append("text").attr("x", right ? -8 : 8).attr("y", 16 + k * 15).attr("font-size", 11)
        .attr("text-anchor", right ? "end" : "start").attr("fill", ink.label)
        .attr("font-weight", k === 0 ? 700 : 400).text(l);
    });
    const w = 8 + Math.max(...lines.map((l) => l.length)) * 6.6 + 8;
    bg.attr("x", right ? -w : 0).attr("width", w).attr("height", 10 + lines.length * 15);
  };

  createEffect(() => {
    void props.race; void props.sources; void props.leg; void isDark();
    geom();
    draw();
  });
  createEffect(() => { void props.cursor; drawCursor(); });
  onCleanup(() => { if (svgRef) {d3.select(svgRef).selectAll("*").remove();} });

  return (
    <div class="race-dtl-chart">
      <svg ref={svgRef} class="race-dtl-svg" role="img" aria-label={`Race ${props.race.race} distance to leader over time`} />
      <div class="race-chart-legend">
        <For each={geom().boats}>
          {(s) => (
            <span class="race-chart-legend-item">
              <span class="race-chart-legend-swatch" style={{ background: sourcesStore.getSourceColor(s) || "var(--color-text-secondary)" }} />
              {s}
            </span>
          )}
        </For>
        <span class="race-chart-legend-note">T tack · G gybe · R round up · B bear away</span>
        <Show when={props.onSeek}>
          <span class="race-chart-hint">Click to open the map at that moment</span>
        </Show>
      </div>
    </div>
  );
}
